export const GUIDE_QUALITY_RUBRIC = [
  {
    id: 'content_reliability',
    label: 'Content reliability',
    shortLabel: 'Reliability',
    weight: 20,
    description: 'Internal factual consistency, credible and appropriately qualified claims, responsible cultural framing, and a clear distinction between fact, interpretation, and legend.',
    assessorGuidance: 'Check for contradictions, suspicious precision, unsupported certainty, anachronisms, and claims that should be source-verified. Do not assume an unsupported claim is true or false.'
  },
  {
    id: 'content_value_storytelling',
    label: 'Content value & storytelling',
    shortLabel: 'Storytelling',
    weight: 20,
    description: 'Interesting narrative, distinctive local insight, memorable detail, human perspective, and meaningful value for the visitor.',
    assessorGuidance: 'Reward purposeful stories, concrete detail, and emotional or intellectual payoff. Penalize generic filler, encyclopedia-style listing, empty superlatives, repetition, and tangents that do not help the visitor understand the subject.'
  },
  {
    id: 'spoken_audio_quality',
    label: 'Spoken-audio quality',
    shortLabel: 'Spoken audio',
    weight: 15,
    description: 'Natural spoken language, clear sentences, suitable pacing, pronunciation awareness, and text that works without a screen.',
    assessorGuidance: 'Read the narration as spoken audio. Penalize overlong sentences, dense date/name sequences, visual-only references, awkward punctuation, and overly long or uneven stops.'
  },
  {
    id: 'context_narrative_coherence',
    label: 'Context & narrative coherence',
    shortLabel: 'Coherence',
    weight: 15,
    description: 'A guide-level frame that helps a first-time visitor understand the place as a whole, situate independent stories, and build knowledge across the experience.',
    assessorGuidance: 'Assess whether the opening establishes what the place is and why it matters; whether essential historical, geographical, or thematic context arrives before it is needed; whether separate stories remain anchored to the wider subject; and whether the guide offers progression or synthesis. Do not require an artificial plot linking every stop.'
  },
  {
    id: 'tour_stop_experience',
    label: 'Tour & stop experience',
    shortLabel: 'Tour design',
    weight: 10,
    description: 'A logical stop order, useful transitions and orientation, balanced stop pacing, and a clear purpose for every stop.',
    assessorGuidance: 'Assess the practical visitor journey rather than requiring every story to form one plot. Check whether each stop earns its place, the sequence is usable, transitions help orientation, and stop lengths and density are balanced.'
  },
  {
    id: 'audience_client_alignment',
    label: 'Audience & client alignment',
    shortLabel: 'Audience / client',
    weight: 15,
    description: 'Fit for the target visitor and guide purpose, including clarity, inclusivity, brand voice, commissioned objectives, required topics, and sensitivities when a client brief is available.',
    assessorGuidance: 'Always assess audience fit, knowledge level, explained jargon, inclusive language, and respectful framing. When an explicit client brief is supplied, also assess its objectives, voice, must-include topics, and content boundaries. Never infer hidden client preferences.'
  },
  {
    id: 'publication_readiness',
    label: 'Publication readiness',
    shortLabel: 'Readiness',
    weight: 5,
    description: 'Complete and consistent guide content with no placeholders, obvious omissions, editorial debris, or unresolved issues that block publication.',
    assessorGuidance: 'Check guide and stop completeness, introductions, terminology, formatting, practical information where relevant, and visible items requiring human review. Judge the supplied content only; do not claim to assess audio recording quality or assets that were not provided.'
  }
];

export const GUIDE_QUALITY_SCORE_BANDS = [
  {
    min: 90,
    label: 'Excellent',
    readiness: 'Ready to publish',
    color: 'emerald'
  },
  {
    min: 80,
    label: 'Very good',
    readiness: 'Minor refinements',
    color: 'green'
  },
  {
    min: 70,
    label: 'Good',
    readiness: 'Needs polish',
    color: 'blue'
  },
  {
    min: 60,
    label: 'Fair',
    readiness: 'Substantive improvements',
    color: 'amber'
  },
  {
    min: 0,
    label: 'Needs work',
    readiness: 'Not ready to publish',
    color: 'red'
  }
];

export const GUIDE_QUALITY_BENCHMARKS = [
  { range: '9–10', label: 'Exceptional', description: 'Publication-quality work with only optional refinements.' },
  { range: '7–8', label: 'Strong', description: 'Effective content with identifiable opportunities to polish.' },
  { range: '5–6', label: 'Adequate', description: 'Usable foundations, but important weaknesses reduce quality.' },
  { range: '3–4', label: 'Weak', description: 'Major editorial problems require substantial revision.' },
  { range: '0–2', label: 'Unusable', description: 'Missing, unreliable, or ineffective content.' }
];

export const getGuideQualityBand = (score) => {
  const numericScore = Number(score);
  const safeScore = Number.isFinite(numericScore) ? numericScore : 0;
  return GUIDE_QUALITY_SCORE_BANDS.find((band) => safeScore >= band.min)
    || GUIDE_QUALITY_SCORE_BANDS[GUIDE_QUALITY_SCORE_BANDS.length - 1];
};

export const getGuideQualityCriterion = (criterionId) => (
  GUIDE_QUALITY_RUBRIC.find((criterion) => criterion.id === criterionId) || null
);
